import { useState } from 'react';
import { ErrorInfo } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Home, RefreshCw, ChevronDown, ChevronUp, Copy, Check } from 'lucide-react';
import { Button } from '@/components/Common/button';

interface ErrorFallbackProps {
  error: Error | null;
  errorInfo?: ErrorInfo | null;
  onReset: () => void;
}

export default function ErrorFallback({ error, errorInfo, onReset }: ErrorFallbackProps) {
  const { t } = useTranslation();
  const [showDetails, setShowDetails] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleGoHome = () => {
    onReset();
    window.location.href = '/welcome';
  };

  const handleRetry = () => {
    onReset();
  };

  const buildDiagnosticText = () => {
    const lines: string[] = [
      `URL: ${window.location.href}`,
      `Time: ${new Date().toISOString()}`,
      `User-Agent: ${navigator.userAgent}`,
      '',
      `Error: ${error?.message ?? 'unknown'}`,
    ];
    if (error?.stack) {
      lines.push('', 'Stack:', error.stack);
    }
    if (errorInfo?.componentStack) {
      lines.push('', 'Component stack:', errorInfo.componentStack);
    }
    return lines.join('\n');
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(buildDiagnosticText());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // fallback: select a textarea
    }
  };

  const hasDetails = !!(error?.stack || errorInfo?.componentStack);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="max-w-xl w-full text-center space-y-6">
        <div className="flex justify-center">
          <div className="rounded-full bg-destructive/10 p-4">
            <AlertTriangle className="h-12 w-12 text-destructive" />
          </div>
        </div>

        <div className="space-y-2">
          <h1 className="text-2xl font-bold text-foreground">
            {t('errorBoundary.title')}
          </h1>
          <p className="text-muted-foreground">
            {t('errorBoundary.description')}
          </p>
        </div>

        {error?.message && (
          <div className="rounded-md bg-muted px-4 py-3 text-left">
            <p className="text-xs font-mono text-muted-foreground break-all">
              {error.message}
            </p>
          </div>
        )}

        {hasDetails && (
          <div className="rounded-md border border-border text-left">
            <button
              type="button"
              onClick={() => setShowDetails((v) => !v)}
              className="w-full flex items-center justify-between px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              <span>{t('errorBoundary.technicalDetails')}</span>
              {showDetails ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>

            {showDetails && (
              <div className="border-t border-border px-4 py-3 space-y-3">
                {errorInfo?.componentStack && (
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground mb-1">{t('errorBoundary.componentStack')}</p>
                    <pre className="text-xs font-mono text-muted-foreground whitespace-pre-wrap break-all max-h-48 overflow-y-auto">
                      {errorInfo.componentStack.trim()}
                    </pre>
                  </div>
                )}
                {error?.stack && (
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground mb-1">{t('errorBoundary.jsStack')}</p>
                    <pre className="text-xs font-mono text-muted-foreground whitespace-pre-wrap break-all max-h-48 overflow-y-auto">
                      {error.stack}
                    </pre>
                  </div>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCopy}
                  className="gap-2 w-full"
                >
                  {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                  {copied ? t('errorBoundary.copied') : t('errorBoundary.copyDiagnostics')}
                </Button>
              </div>
            )}
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Button variant="outline" onClick={handleRetry} className="gap-2">
            <RefreshCw className="h-4 w-4" />
            {t('errorBoundary.retry')}
          </Button>
          <Button onClick={handleGoHome} className="gap-2">
            <Home className="h-4 w-4" />
            {t('errorBoundary.backToDashboard')}
          </Button>
        </div>
      </div>
    </div>
  );
}
